import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import type { EarningsSummary } from "@/types/affiliate-dashboard";
import { affiliateDashboardApi } from "@/services/affiliateDashboard.service";
import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { SetMonthlyGoalModal } from "@/components/affiliate/SetMonthlyGoalModal";
import { money } from "./helpers";

function daysLeftInMonth() {
  const now = new Date();
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return Math.max(1, last - now.getDate() + 1);
}

/**
 * The affiliate's own target for the month, across every brand. A hairline
 * bar and one sentence on pace; the modal to change it is the existing one.
 */
export function GoalCard({ summary }: { summary: EarningsSummary | undefined }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const update = useMutation({
    mutationFn: (monthlyGoal: number) => affiliateDashboardApi.updateMonthlyGoal({ monthlyGoal }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliate-earnings-summary"] });
      setOpen(false);
      toast.success("Goal saved");
    },
    onError: () => toast.error("Couldn't save the goal. Try again."),
  });

  const goal = summary?.monthlyGoal ?? 0;
  const earned = summary?.thisMonth ?? 0;
  const hasGoal = goal > 0;
  const progress = hasGoal ? Math.min(100, (earned / goal) * 100) : 0;
  const remaining = Math.max(0, goal - earned);
  const daysLeft = daysLeftInMonth();

  return (
    <DashboardCard
      id="dashboard-goal-card"
      title="Monthly goal"
      action={
        summary && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded-sm text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {hasGoal ? "Edit goal" : "Set goal"}
          </button>
        )
      }
      bodyClassName="px-5 pb-5"
    >
      {hasGoal ? (
        <>
          <div className="flex items-baseline gap-1.5">
            <span className="text-3xl font-semibold tabular-nums tracking-tight text-foreground">{money(earned)}</span>
            <span className="text-sm text-muted-foreground">of {money(goal)}</span>
          </div>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
            className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-muted"
          >
            <div className="h-full rounded-full bg-brand-600" style={{ width: `${progress}%` }} />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {remaining === 0
              ? `Goal reached with ${daysLeft} day${daysLeft === 1 ? "" : "s"} to spare.`
              : `${money(remaining)} to go, about ${money(remaining / daysLeft)} a day for the ${daysLeft} day${daysLeft === 1 ? "" : "s"} left.`}
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          {summary
            ? "Set a target for the month and this card tracks your pace toward it."
            : "Goal progress couldn't load."}
        </p>
      )}
      {summary && (
        <SetMonthlyGoalModal
          open={open}
          onOpenChange={setOpen}
          currentGoal={goal}
          onGoalUpdate={(value) => update.mutate(value)}
          isLoading={update.isPending}
        />
      )}
    </DashboardCard>
  );
}
